import {
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { BehaviorSubject } from 'rxjs';
import { FloatLabelType } from '@angular/material/form-field';
import { IContactModel } from '@fi-shared/models/common';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';
import { FiContactBaseDirective } from '../fi-contact-base.directive';
import { DataFieldPropertyType } from '@fi-app/data-field/data-field-property-type';
import {
    fiValidateDropNumberString,
    fiValidateInputNumberString,
    fiValidatePasteNumberString,
} from '@fi-shared/utils/fi-validate-input-number-string';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-phone-number-contact',
    templateUrl: './phone-number-contact.component.html',
    styleUrls: ['./phone-number-contact.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-phone-number-contact' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PhoneNumberContactComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: PhoneNumberContactComponent,
        },
    ],
    standalone: false,
})
export class PhoneNumberContactComponent extends FiContactBaseDirective implements OnInit, OnDestroy, OnChanges {
    @Input() phoneNumberPropertyName: string = 'ContactValue';
    @Input() countryCodePropertyName: string = 'CountryId';
    @Input() isCountryCodeNewLayout: boolean;
    @Input() phoneNumberLabel: string = 'common.phone_number';
    @Input() userId: number;
    @Input() preventUniqueContactCheck: boolean;
    @Input() isFormFieldNewDefaultStyle: boolean;
    dataFieldPropertyType = DataFieldPropertyType.PhoneNumber;

    dataForUniquePhoneNumberCheck$ = new BehaviorSubject<IFiUniqueContactData>({} as IFiUniqueContactData);
    countries$ = new BehaviorSubject<ICountryModel[]>([]);

    fiValidateInputNumberString = fiValidateInputNumberString;
    fiValidatePasteNumberString = fiValidatePasteNumberString;
    fiValidateDropNumberString = fiValidateDropNumberString;

    beforeValidityUpdateFn: () => void = () => this.onCountryIdChanged(this.contact.CountryId, false);

    get contact(): IContactModel {
        return this.ngModel;
    }

    constructor(
        injector: Injector,
        private countryHelperService: FiCountryHelperService,
        private detectDevicesService: DetectDevicesService,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.contacts) {
            this.setDataForUniquePhoneNumberCheck();
        }
    }

    ngOnInit() {
        this.setCountriesData();
        this.setPrefixValueCheck();
        super.ngOnInit();
        this.updateDataForUniqueContactCheck$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.setDataForUniquePhoneNumberCheck());
    }

    isMobileSmall(): boolean {
        return this.detectDevicesService.isMobileSmall();
    }

    isCountryRequired(): boolean {
        return this.isRequired || !!this.contact[this.phoneNumberPropertyName];
    }

    private setCountriesData() {
        this.countryHelperService.getCountryCodes().then((data: ICountryModel[]) => {
            this.countries$.next(data);
            if (this.contact[this.countryCodePropertyName]) {
                this.onCountryIdChanged(this.contact[this.countryCodePropertyName], false);
            }
        });
    }

    onCountryIdChanged(countryId: number, throwChangedEvent = true) {
        const selectedCountry: ICountryModel = this.countries$.value.find((item) => item.Id === countryId);
        this.contact.ExtendedValue = selectedCountry?.CountryCode ?? '';
        //this.contact.FullContactValue = `(${this.contact.ExtendedValue})${this.contact[this.phoneNumberPropertyName]}`;
        this.setPrefixValueCheck();
        if (throwChangedEvent) {
            this.onPhoneNumberChanged();
        }
    }

    private contactPrefixValue: string;
    setPrefixValueCheck() {
        if (this.contactPrefixValue === this.contact.ExtendedValue) return;

        this.contactPrefixValue = this.contact.ExtendedValue;
        this.setDataForUniquePhoneNumberCheck();
    }

    private setDataForUniquePhoneNumberCheck() {
        this.dataForUniquePhoneNumberCheck$.next({
            userId: this.userId,
            doCheck: !this.preventUniqueContactCheck,
            throwOnlyWhenContactUsedAsLogin: !this.contact.IsSignIn,
            prefixValue: this.contactPrefixValue,
            isDuplicate: this.isContactDuplicate,
            isSignIn: this.contact.IsSignIn,
        });
    }

    getFloatLabelType(): FloatLabelType {
        return this.isCountryCodeSelected() ? 'always' : 'auto';
    }

    isCountryCodeSelected(): boolean {
        return !!this.contact.ExtendedValue?.length;
    }

    onPhoneNumberChanged() {
        this.setPrefixValueCheck();
        this.onContactChange();
    }
}
